import React from "react";
import { t } from "../i18n";

/** Resolve human labels for stored category codes using loaded options. */
export function labelsForCategoryCodes(options, codes) {
  if (!Array.isArray(codes) || codes.length === 0) return [];
  const map = new Map((options || []).map((o) => [String(o.code).toUpperCase(), o.label]));
  return codes.map((c) => map.get(String(c).toUpperCase()) || String(c));
}

/**
 * Organized multi-select for canonical store categories (registration + portal edit),
 * and similar flows (e.g. optional vehicle standards on Ready orders).
 * Options may use `label` or `title`; optional `description` renders under the title.
 *
 * @param {{
 *   options: { code: string, label?: string, title?: string, description?: string }[],
 *   selected: Set<string>,
 *   onToggle: (code: string) => void,
 *   disabled?: boolean,
 *   theme?: 'register' | 'portal',
 *   idPrefix?: string,
 *   hideTitle?: boolean,
 *   hiddenTitleHelperText?: string,
 *   ariaGroupLabel?: string,
 *   emptyMessage?: string,
 *   selectionKeyMode?: 'upper' | 'preserve',
 *   showOptionCode?: boolean,
 * }} props
 */
export function MerchantCategoryPicker({
  options,
  selected,
  onToggle,
  disabled = false,
  theme = "portal",
  idPrefix = "cat",
  hideTitle = false,
  hiddenTitleHelperText,
  ariaGroupLabel,
  emptyMessage,
  selectionKeyMode = "upper",
  showOptionCode = false,
}) {
  const isRegister = theme === "register";
  const boxStyle = isRegister
    ? {
        marginTop: 10,
        padding: 16,
        borderRadius: 10,
        border: "1px solid #e6e1f0",
        backgroundColor: "#f1eef8",
      }
    : {
        marginTop: 10,
        padding: 16,
        borderRadius: 12,
        border: "1px solid var(--border-color)",
        background: "var(--primary-light)",
      };
  const titleColor = isRegister ? "#6b4eaa" : "var(--text-primary)";
  const subColor = isRegister ? "#8c8595" : "var(--text-secondary)";
  const tileBase = isRegister
    ? {
        padding: "10px 12px",
        borderRadius: 14,
        border: "1px solid #e6e1f0",
        backgroundColor: "#fcfbfe",
        color: "#6b4eaa",
        fontSize: 13,
        cursor: disabled ? "default" : "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        opacity: disabled ? 0.55 : 1,
        transition: "border-color 0.15s, background 0.15s",
      }
    : {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid var(--border-color)",
        background: "var(--primary-dark)",
        color: "var(--text-primary)",
        fontSize: 13,
        cursor: disabled ? "default" : "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        opacity: disabled ? 0.55 : 1,
        transition: "border-color 0.15s, background 0.15s",
      };

  const selectedStyle = isRegister
    ? { borderColor: "#8467c2", backgroundColor: "#f1eef8", boxShadow: "0 0 0 1px rgba(107, 78, 170, 0.35)" }
    : { borderColor: "var(--accent-primary)", boxShadow: "0 0 0 1px rgba(107, 78, 170, 0.25)" };

  const count = selected?.size ?? 0;

  return (
    <div style={boxStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: hideTitle ? "center" : "baseline", gap: 12, flexWrap: "wrap" }}>
        {!hideTitle ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: titleColor }}>{t("store.categories")}</div>
            <div style={{ fontSize: 12, color: subColor, marginTop: 4 }}>{t("categories.hint")}</div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: subColor }}>{hiddenTitleHelperText || t("categories.choose")}</div>
        )}
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: isRegister ? "#8467c2" : "var(--accent-primary)",
            padding: "4px 10px",
            borderRadius: 999,
            border: isRegister ? "1px solid #e6e1f0" : "1px solid var(--border-color)",
            background: isRegister ? "#fcfbfe" : "var(--primary-dark)",
          }}
        >
          {t("readymap.n_selected", { n: count })}
        </div>
      </div>
      {!options?.length ? (
        <p style={{ marginTop: 12, fontSize: 12, color: subColor }}>{emptyMessage || t("categories.loading")}</p>
      ) : (
        <div
          role="group"
          aria-label={ariaGroupLabel || t("store.categories")}
          style={{
            marginTop: 14,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 10,
          }}
        >
          {options.map((opt) => {
            const code = opt.code;
            const key =
              selectionKeyMode === "preserve" ? String(code) : String(code).toUpperCase();
            const isOn = selected.has(key);
            const primaryLabel = opt.label ?? opt.title ?? opt.code;
            const description = typeof opt.description === "string" ? opt.description : "";
            return (
              <button
                key={code}
                type="button"
                id={`${idPrefix}-${key.replace(/\s+/g, "-")}`}
                disabled={disabled}
                onClick={() => !disabled && onToggle(code)}
                style={{
                  ...tileBase,
                  ...(isOn ? selectedStyle : {}),
                  textAlign: "left",
                  width: "100%",
                  fontFamily: "inherit",
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    marginTop: 2,
                    borderRadius: 10,
                    border: isRegister ? "2px solid #8c8595" : "2px solid var(--border-color)",
                    background: isOn ? (isRegister ? "#8467c2" : "var(--accent-primary)") : "transparent",
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-hidden
                >
                  {isOn ? <span style={{ color: "#1b1530", fontSize: 11, fontWeight: 800 }}>✓</span> : null}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: isOn ? 600 : 500 }}>{primaryLabel}</span>
                  {showOptionCode && code ? (
                    <code
                      style={{
                        display: "block",
                        fontSize: 11,
                        fontFamily: "ui-monospace, monospace",
                        color: subColor,
                        marginTop: 3,
                      }}
                    >
                      {code}
                    </code>
                  ) : null}
                  {description ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: subColor,
                        marginTop: 4,
                        lineHeight: 1.35,
                        fontWeight: 400,
                      }}
                    >
                      {description}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Read-only chips for view mode (portal theme). */
export function MerchantCategoryChips({ labels }) {
  if (!labels?.length) {
    return <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>-</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {labels.map((text) => (
        <span
          key={text}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 12px",
            borderRadius: 999,
            border: "1px solid var(--border-color)",
            background: "var(--primary-dark)",
            color: "var(--text-primary)",
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

/** Read-only chips for registration completion preview (dark theme). - unused but available */
export function MerchantCategoryChipsDark({ labels }) {
  if (!labels?.length) return <span style={{ fontSize: 13, color: "#8c8595" }}>-</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {labels.map((text) => (
        <span
          key={text}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 12px",
            borderRadius: 999,
            border: "1px solid #e6e1f0",
            background: "#fcfbfe",
            color: "#6b4eaa",
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}
